import {
  Component,
  OnInit,
  signal,
  computed,
  effect,
  inject,
  AfterViewInit,
  OnDestroy,
} from '@angular/core';
import { CommonModule } from '@angular/common';
import { ActivatedRoute, Router } from '@angular/router';
import {
  FormBuilder,
  FormGroup,
  ReactiveFormsModule,
  Validators,
  FormsModule,
} from '@angular/forms';
import { LineAssociation, CustomerUser, AttachRequest } from '../../models/line-association.model';
import { LineAssociationService } from '../../services/line-association.service';
import { SmsEvidentService } from '../../services/sms-evident.service';
import { PostMessageService } from '../../services/post-message.service';
import { InputMaskModule } from 'primeng/inputmask';
import { environment } from '../../../environments/environment';
import { AppModeService } from '../../services/app-mode-service';
import { LoadingUtilsService } from '@/app/services/utils/loading-utils-service';
import { AppButton } from '@/app/core/components/app-button/app-button';
import { FnfBaseDialog } from '@/app/core/components/friends-n-family/fnf-base-dialog/fnf-base-dialog';
import { FnfButtons } from '@/app/core/components/friends-n-family/fnf-buttons/fnf-buttons';
import { getApiErrorMessage } from '@/app/core/utils/api-error-message';
import { IframeResizeHelper } from '@/app/core/helpers/iframe-resize';
import { formatMsisdn } from '@/app/core/utils/number-format';
import { FnfActionDialog } from '@/app/core/components/friends-n-family/fnf-action-dialog/fnf-action-dialog';
import { FnfSuccessDialog } from '@/app/core/components/friends-n-family/fnf-success-dialog/fnf-success-dialog';
import { CustomerService } from '@/app/services/customer-service';
import { MessageModule } from 'primeng/message';
import { Subscription } from 'rxjs';
import { LinesView, getVisibleLines } from './lines-management.utils';

/**
 * Componente principal de gestión de líneas
 * Permite listar, crear, editar y eliminar líneas asociadas al usuario
 * Diseñado para ser embebido vía iframe
 */
@Component({
  selector: 'app-lines-management',
  imports: [
    CommonModule,
    ReactiveFormsModule,
    FormsModule,
    InputMaskModule,
    FnfBaseDialog,
    FnfButtons,
    AppButton,
    FnfActionDialog,
    FnfSuccessDialog,
    MessageModule,
  ],
  templateUrl: './lines-management.component.html',
  styleUrl: './lines-management.component.scss',
  standalone: true,
})
export class LinesManagementComponent implements OnInit, AfterViewInit, OnDestroy {
  private iframeResizeHelper = new IframeResizeHelper(200); // 200ms de throttle
  private loading = inject(LoadingUtilsService);
  private customerService = inject(CustomerService);
  value: string = '';

  // Señales de estado
  protected allLines = signal<LineAssociation[]>([]);
  protected customerUser = signal<CustomerUser | null>(null);
  protected error = signal<string | null>(null);

  private route = inject(ActivatedRoute);
  protected viewMode = computed<LinesView>(() => {
    const view = this.route.snapshot.data['view'];
    return view === 'all' || view === 'mi-parche' ? view : 'legacy';
  });
  protected isLegacyView = computed(() => this.viewMode() === 'legacy');
  protected isMiParcheView = computed(() => this.viewMode() === 'mi-parche');
  protected linesList = computed(() => getVisibleLines(this.allLines(), this.viewMode()));

  // Estados del modal de añadir línea
  protected showAddLineModal = signal<boolean>(false);
  protected validMsisdn = signal<boolean>(false);
  protected codeSended = signal<boolean>(false);
  protected lblButtonSendCode = signal<string>('Enviar');

  // Estado de modales de confirmación
  protected showDeleteModal = signal<boolean>(false);
  protected lineToDelete = signal<LineAssociation | null>(null);

  // Estados de éxito
  protected showSuccessModal = signal<boolean>(false);
  protected successMessage = signal<string>('');

  // Formularios
  protected newLineForm: FormGroup;

  // Computed signals
  protected hasLines = computed(() => this.linesList().length > 0);
  protected isEmbedded = computed(() => this.appModeService.isEmbedded());

  protected isFFActive = environment.activeFF;

  fnfActionDialogMeta = signal({
    subject: 'Eliminar Línea',
    title: '¿Estás seguro de eliminar esta línea?',
    description: 'Estás a punto de eliminar la línea ${msisdn} ¿Deseas continuar?',
    buttonText: 'ELIMINAR',
    isLoading: false,
    error: '',
  });

  fnfSuccessDialogMeta = signal({
    title: '¡Línea creada correctamente!',
    description: 'Tu línea se ha agregado correctamente.',
    buttonText: 'Continuar',
  });

  protected token: string | null | undefined;
  private routeSubscription: Subscription | undefined;

  constructor(
    private fb: FormBuilder,
    private router: Router,
    private lineAssociationService: LineAssociationService,
    private smsService: SmsEvidentService,
    private postMessageService: PostMessageService,
    private appModeService: AppModeService
  ) {
    // Inicializar formulario
    this.newLineForm = this.fb.group({
      newMsisdn: ['', [Validators.required, Validators.pattern(/^\d{10}$/)]],
      smsCode: ['', [Validators.required, Validators.minLength(7), Validators.maxLength(7)]],
    });

    // Effect para cargar datos cuando el token esté disponible
    effect(() => {
      const token = this.postMessageService.getAccessToken();
      if (token) {
        !environment.production &&
          console.log('[LinesManagement] ✅ Token recibido, cargando líneas...');
        this.loadLines();
      } else {
        !environment.production && console.log('[LinesManagement] ⏳ Esperando token...');
      }
    });
  }

  ngOnInit(): void {}

  ngAfterViewInit() {
    this.iframeResizeHelper.init();
  }

  ngOnDestroy() {
    this.iframeResizeHelper.destroy();
    if (this.routeSubscription) {
      this.routeSubscription.unsubscribe();
    }
  }

  /**
   * Carga las líneas del usuario
   */
  protected loadLines(): void {
    this.error.set(null);

    this.loading.track('boot', this.customerService.getUser()).subscribe({
      next: (response) => {
        if (response.success && response.data) {
          const { id, userId: email, type, lines } = response.data;
          this.allLines.set(lines ?? []);

          this.customerUser.set({
            id: id,
            userId: email,
            name: email,
            email: email,
            type: type as 'RESIDENTIAL' | 'BUSINESS',
            lines: lines,
          });
        }
      },
      error: (err) => {
        !environment?.production && console.error('Error cargando líneas:', err);
        this.error.set(
          getApiErrorMessage(
            err,
            'Ocurrió un error inesperado al obtener las líneas. Por favor, intenta de nuevo.'
          )
        );
      },
    });
  }

  /**
   * Abre el modal para añadir nueva línea
   */
  protected openAddLineModal(): void {
    this.showAddLineModal.set(true);
    this.resetAddLineForm();
  }

  /**
   * Cierra el modal de añadir línea
   */
  protected closeAddLineModal(): void {
    this.showAddLineModal.set(false);
    this.resetAddLineForm();
  }

  /**
   * Resetea el formulario de añadir línea
   */
  private resetAddLineForm(): void {
    this.newLineForm.reset();
    this.validMsisdn.set(false);
    this.codeSended.set(false);
    this.lblButtonSendCode.set('Enviar');
    this.error.set(null);
  }

  /**
   * Valida el número MSISDN ingresado
   */
  protected validateNewMsisdn(): void {
    const msisdnControl = this.newLineForm.get('newMsisdn');
    if (!msisdnControl?.value || msisdnControl.value.length !== 10) {
      return;
    }

    // Verificar si ya existe
    const exists = this.allLines().some(
      (line) => line.msisdn === msisdnControl.value || line.msisdn === `57${msisdnControl.value}`
    );

    if (exists) {
      msisdnControl.setErrors({ exists: true });
      this.error.set('Esta línea ya está asociada a tu cuenta');
      return;
    }

    const msisdn = `57${msisdnControl.value}`;

    this.loading.track('action', this.lineAssociationService.validateMsisdn(msisdn)).subscribe({
      next: (response) => {
        if (response.data?.responseCode === 'OK' && response.data?.state === 'ACTIVE') {
          msisdnControl.setErrors(null);
          this.validMsisdn.set(true);
          this.error.set(null);
        } else if (response.data?.responseCode === 'INVALID_ARGUMENTS') {
          msisdnControl.setErrors({ invalid: true });
          this.error.set('La línea no es Virgin Mobile');
        } else {
          msisdnControl.setErrors({ inactive: true });
          this.error.set('La línea no está activa');
        }
      },
      error: (err) => {
        msisdnControl.setErrors({ sys_error: true });
        this.error.set(getApiErrorMessage(err, 'Ocurrió un error inesperado al validar la línea'));
      },
    });
  }

  /**
   * Envía el código SMS de validación
   */
  protected sendSMSCode(): void {
    const msisdn = this.newLineForm.get('newMsisdn')?.value;
    if (!msisdn) return;

    this.codeSended.set(false);
    this.error.set(null);

    this.loading.track('action', this.smsService.generateCode({ phoneNumber: msisdn })).subscribe({
      next: (response) => {
        if (response.success) {
          this.lblButtonSendCode.set('Reenviar');
          this.codeSended.set(true);
        } else {
          this.error.set(
            getApiErrorMessage(
              response,
              'No se pudo enviar el código SMS, por favor intenta nuevamente.'
            )
          );
        }
      },
      error: (err) => {
        this.error.set(
          getApiErrorMessage(err, 'Ocurrió un error inesperado al enviar el código SMS')
        );
      },
    });
  }

  /**
   * Valida el código SMS y crea la asociación de línea
   */
  protected validateCodeSMS(): void {
    const msisdn = this.newLineForm.get('newMsisdn')?.value;
    const code = this.newLineForm.get('smsCode')?.value;

    if (!msisdn || !code) return;

    this.error.set(null);

    this.loading
      .track('action', this.smsService.validateCode({ phoneNumber: msisdn, code }))
      .subscribe({
        next: (response) => {
          if (response.success) {
            this.createLineAssociation();
          } else {
            this.error.set(
              getApiErrorMessage(
                response,
                'No se pudo validar el código SMS, por favor intenta nuevamente.'
              )
            );
          }
        },
        error: (err) => {
          const errorCode = err?.error?.apiError?.code;
          if (errorCode?.endsWith('14')) {
            this.error.set('Código inválido. Verifica e intenta nuevamente.');
          } else {
            this.error.set(
              getApiErrorMessage(err, 'Ocurrió un error inesperado al validad el código.')
            );
          }
        },
      });
  }

  /**
   * Crea la asociación de línea
   */
  private createLineAssociation(): void {
    const msisdn = this.newLineForm.get('newMsisdn')?.value;
    const customer = this.customerUser();

    if (!msisdn || !customer) return;

    const request: AttachRequest = {
      user: customer.userId,
      msisdn: this.formatMsisdn(msisdn),
      alias: msisdn,
      owner: false,
      principal: this.allLines().length === 0, // Primera línea es principal
      customerId: customer.id,
    };

    this.loading
      .track('action', this.lineAssociationService.createLineAssociation(request))
      .subscribe({
        next: (response) => {
          if (response.success) {
            this.closeAddLineModal();
            this.showSuccessModal.set(true);
            this.fnfSuccessDialogMeta.set({
              title: '¡Línea agregada correctamente!',
              description: 'Tu línea se ha agregado correctamente.',
              buttonText: 'Continuar',
            });
            this.loadLines();

            // Notificar al host
            this.postMessageService.emitEvent('lineAdded', {
              msisdn: request.msisdn,
            });
          } else {
            this.error.set(
              getApiErrorMessage(
                response,
                'No se pudo agregar la línea, por favor intenta nuevamente.'
              )
            );
          }
        },
        error: (err) => {
          this.error.set(
            getApiErrorMessage(err, 'Ocurrió un error inesperado al agregar la línea')
          );
        },
      });
  }

  /**
   * Abre modal de confirmación para eliminar línea
   */
  protected MASK_MSISDN(msisdn: string): string {
    const maskMsisdn =
      '( ' +
      msisdn.toString().substring(0, 3) +
      ' ) ' +
      msisdn.substring(3, 6) +
      ' - ' +
      msisdn.substring(6, 10);
    return maskMsisdn;
  }

  protected openDeleteConfirmation(line: LineAssociation): void {
    this.lineToDelete.set(line);
    this.showDeleteModal.set(true);
    const description = this.fnfActionDialogMeta().description.replace(
      '${msisdn}',
      this.MASK_MSISDN(line.msisdn)
    );
    this.fnfActionDialogMeta.update((current) => ({ ...current, description, error: '' }));
  }

  /**
   * Elimina una línea
   */
  protected deleteLineConfirmed(): void {
    const line = this.lineToDelete();
    const customer = this.customerUser();

    if (!line || !customer) return;

    this.loading
      .track(
        'action',
        this.lineAssociationService.deleteByUserAndMsisdn({
          msisdn: formatMsisdn(line.msisdn),
          customerId: customer.id,
        })
      )
      .subscribe({
        next: (response) => {
          if (response.success) {
            this.showDeleteModal.set(false);
            this.lineToDelete.set(null);
            this.showSuccessModal.set(true);
            this.fnfSuccessDialogMeta.set({
              title: '¡Línea eliminada correctamente!',
              description: 'Tu línea se ha eliminado correctamente.',
              buttonText: 'Continuar',
            });
            this.loadLines();

            // Notificar al host
            this.postMessageService.emitEvent('lineDeleted', {
              msisdn: line.msisdn,
            });
          } else {
            const error = getApiErrorMessage(
              response,
              'No se pudo eliminar la línea, por favor intenta nuevamente.'
            );
            this.fnfActionDialogMeta.update((current) => ({ ...current, error: error }));
            this.error.set(error);
          }
        },
        error: (err) => {
          const error = getApiErrorMessage(err, 'Ocurrió un error inesperado al eliminar la línea');
          this.fnfActionDialogMeta.update((current) => ({ ...current, error: error }));
          this.error.set(error);
        },
      });
  }

  /**
   * Navega a la página de edición de la línea
   */
  protected editLine(line: LineAssociation): void {
    this.router.navigate(['/embed/lines/edit', line.id], { queryParamsHandling: 'preserve' });
  }

  /**
   * Formatea número MSISDN para visualización
   */
  protected formatMsisdn(msisdn: string): string {
    // Remover código de país si existe
    return formatMsisdn(msisdn, 12);
  }

  protected getFnfDiscount(line: LineAssociation): string | number | undefined {
    return this.isLegacyView()
      ? line.friendAndFamily?.discount
      : (line.friendAndFamily?.discount ?? '0%');
  }

  protected getFnfCount(line: LineAssociation): number | undefined {
    return this.isLegacyView() ? line.friendAndFamily?.count : (line.friendAndFamily?.count ?? 0);
  }

  goToFnFDetails(number: string) {
    const section = this.isMiParcheView()
      ? 'mi-parche'
      : this.isLegacyView()
        ? 'lines'
        : 'lines-v2';
    this.router.navigate([`/embed/${section}/${number}/fnf-details`], {
      queryParamsHandling: 'preserve',
    });
  }
}
